import { ref, Ref } from 'vue'
import type { ApiError } from '@/types/api'

export interface UseErrorHandlerReturn {
  error: Ref<string | null>
  hasError: Ref<boolean>
  setError: (_errorData: unknown) => void
  clearError: () => void
  handleError: <T>(_errorPromise: Promise<T>) => Promise<T | null>
}

export const useErrorHandler = (): UseErrorHandlerReturn => {
  const error = ref<string | null>(null)
  const hasError = ref<boolean>(false)

  const setError = (errorData: unknown): void => {
    if (typeof errorData === 'string') {
      error.value = errorData
    } else if (errorData && typeof errorData === 'object' && 'detail' in errorData) {
      error.value = (errorData as ApiError).detail
    } else if (errorData instanceof Error) {
      error.value = errorData.message
    } else {
      error.value = 'An unknown error occurred'
    }
    hasError.value = true
  }

  const clearError = (): void => {
    error.value = null
    hasError.value = false
  }

  const handleError = async <T>(errorPromise: Promise<T>): Promise<T | null> => {
    try {
      clearError()
      return await errorPromise
    } catch (err) {
      setError(err)
      return null
    }
  }

  return {
    error,
    hasError,
    setError,
    clearError,
    handleError
  }
}
